import React, { useEffect, useState } from "react";
import {
  TableContainer,TableToolbar,TableToolbarContent,TableToolbarSearch,
  Table,TableHead,TableRow,TableHeader,TableBody,
  TableCell,Button,Modal,TextInput,TableToolbarAction,TableToolbarMenu,Dropdown,
} from "@carbon/react";
import { Add } from "@carbon/icons-react";
import { callApi } from "../../../services/apiService";

const headers = [
  { key: "state", header: "State" },
  { key: "country", header: "Country" },
  { key: "status", header: "Status" },
  { key: "createdBy", header: "Created By" },
  { key: "createdAt", header: "Created At" },
  { key: "actions", header: "Actions" },
];

export default function StateMaster() {
  const [rows, setRows] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stateName, setStateName] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);
  const [countries, setCountries] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);

  // Fetch States
  const fetchStates = async () => {
    try {
      const response = await callApi("GET", "/state-masters/");
      const formattedRows = response.data.map((item) => ({
        id: item.id,
        state: item.state_name,
        country: item.country_name,
        countryId: item.country_id,
        status: item.status,
        createdBy: item.created_user,
        createdAt: item.created_at,
      }));
      setRows(formattedRows);
    } catch (error) {
      console.error("Failed to fetch states:", error.message);
    }
  };

  // Fetch Countries
  const fetchCountries = async () => {
    try {
      setIsLoadingCountries(true);
      const response = await callApi("GET", "/countries/");
      const formatted = response.data.map((c) => ({
        id: c.id,
        name: c.c_name,
        code: c.code,
      }));
      setCountries(formatted);
      return formatted;
    } catch (error) {
      console.error("Failed to fetch countries:", error.message);
      return [];
    } finally {
      setIsLoadingCountries(false);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      await fetchStates();
      await fetchCountries(); 
    };
    fetchData();
  }, []);

  // Open modal for Add/Edit
  const openModal = async (row = null) => {
    try {
      let availableCountries = countries;
      if (availableCountries.length === 0) {
        availableCountries = await fetchCountries();
      }

      if (row) {
        // Edit mode
        setStateName(row.state);
        setSelectedRow(row);
        const match = availableCountries.find((c) => c.id === row.countryId);
        setSelectedCountry(match || null);
      } else {
        // Add mode
        setStateName("");
        setSelectedRow(null);
        const india = availableCountries.find(
          (c) => c.name.toLowerCase() === "india"
        );
        setSelectedCountry(india || (availableCountries.length > 0 ? availableCountries[0] : null));
      }

      setIsModalOpen(true);
    } catch (error) {
      console.error("Failed to open modal:", error);
      if (row) {
        setStateName(row.state);
        setSelectedRow(row);
      } else {
        setStateName("");
        setSelectedRow(null);
      }
      setIsModalOpen(true);
    }
  };
  const openAddModal = () => {
    setStateName("");
    setSelectedRow(null);
   
    if (countries.length > 0) {
      const india = countries.find((c) => c.name.toLowerCase() === "india");
      setSelectedCountry(india || countries[0]);
    }
    setIsModalOpen(true);
  };

  const resetModal = () => {
    setIsModalOpen(false);
    setStateName("");
    setSelectedRow(null);
    setSelectedCountry(null);
  };

  // Save (Add / Update)
  const handleSave = async (e) => {
    e.preventDefault();
    if (!stateName.trim()) {
      alert("State name is required");
      return;
    }
    if (!selectedCountry) {
      alert("Please select a country");
      return;
    }

    const payload = {
      STATE_ID: selectedRow ? selectedRow.id : 0,
      STATE_NAME: stateName,
      COUNTRY_ID: selectedCountry.id,
      STATUS: "Active",
      ...(selectedRow
        ? { UPDATED_BY_USER_ID: 1 }
        : { CREATED_BY_USER_ID: 1, UPDATED_BY_USER_ID: 1 }),
    };

    try {
      if (selectedRow) {
        await callApi("PUT", "/state-masters/", payload);
      } else {
        await callApi("POST", "/state-masters/", payload);
      }
      await fetchStates();
      resetModal();
    } catch (error) {
      console.error("Failed to save state:", error.message);
      alert("Failed to save state. Please try again.");
    }
  };

  // Ask before delete
  const confirmDelete = (id) => {
    setDeleteId(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await callApi("DELETE", `/state-masters/${deleteId}`);
      await fetchStates();
    } catch (error) {
      console.error("Failed to delete state:", error.message);
      alert("Failed to delete state. Please try again.");
    } finally {
      setIsDeleteModalOpen(false);
      setDeleteId(null);
    }
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value.toLowerCase();
    console.log("Search:", value);
  };

  return (
    <div>
      <TableContainer title="State Master">
        <TableToolbar>
          <TableToolbarContent>
            <TableToolbarSearch 
              persistent 
              onChange={handleSearch} 
              placeholder="Search states..."
            />
            <Button 
              renderIcon={Add} 
              onClick={openAddModal}
              disabled={isLoadingCountries}
            >
              {isLoadingCountries ? "Loading..." : "Add State"}
            </Button>
          </TableToolbarContent>
        </TableToolbar>

        <Table>
          <TableHead>
            <TableRow>
              {headers.map((header) => (
                <TableHeader key={header.key}>{header.header}</TableHeader>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={headers.length} style={{ textAlign: 'center' }}>
                  No states found
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{row.state}</TableCell>
                  <TableCell>{row.country}</TableCell>
                  <TableCell>
                    <span style={{ 
                      color: row.status 
                    }}>
                      {row.status}
                    </span>
                  </TableCell>
                  <TableCell>{row.createdBy}</TableCell>
                  <TableCell>{row.createdAt}</TableCell>
                  <TableCell>
                    <TableToolbarMenu>
                      <TableToolbarAction onClick={() => openModal(row)}>
                        Edit
                      </TableToolbarAction>
                      <TableToolbarAction onClick={() => confirmDelete(row.id)}>
                        Delete
                      </TableToolbarAction>
                    </TableToolbarMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Modal */}
      <Modal
        open={isModalOpen}
        onRequestClose={resetModal}
        modalHeading={selectedRow ? "Edit State" : "Add State"}
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onRequestSubmit={handleSave}
        primaryButtonDisabled={!stateName.trim() || !selectedCountry || isLoadingCountries}
      >
        <TextInput
          id="state-name"
          labelText="State Name"
          value={stateName}
          onChange={(e) => setStateName(e.target.value)}
          required
          invalid={!stateName.trim()}
          invalidText="State name is required"
          placeholder="Enter state name"
          style={{ marginBottom: '1rem' }}
        />

        <Dropdown
          id="country"
          titleText="Country"
          label="Select Country"
          items={countries}
          itemToString={(item) => (item ? item.name : "")}
          selectedItem={selectedCountry}
          onChange={({ selectedItem }) => setSelectedCountry(selectedItem)}
          disabled={isLoadingCountries}
          helperText={isLoadingCountries ? "Loading countries..." : ""}
          invalid={!selectedCountry}
          invalidText="Please select a country"
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        open={isDeleteModalOpen}
        modalHeading="Confirm Delete"
        primaryButtonText="Delete"
        secondaryButtonText="Cancel"
        danger
        onRequestClose={() => setIsDeleteModalOpen(false)}
        onRequestSubmit={handleConfirmDelete}
      >
        <p>Are you sure you want to delete this state? This action cannot be undone.</p>
      </Modal>
    </div>
  );
}